import Campaign from '../store/Campaign';
import React from 'react';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignQueue } from '../store/CampaignQueue';
import { CampaignType } from '../store/schema/CampaignType';
import { endCampaign } from '../utils/endCampaign';
import { getTooltipContent } from './utils/getTooltipContent';
import { LazyTooltip, Placement } from 'tooltip';

const DEFAULT_TIMEOUT_IN_MILLISECONDS: number = 5000;

/**
 * @description Config which is required to create Tooltip Campaign Type instance
 * @param content: text of the campaignType component
 */
export interface CampaignTooltipConfig {
    content: string;
    title?: string;
    placement: Placement;
    placementRef: React.RefObject<any>;
    refId?: string;
    timeout?: number;
    onRender?: () => void;
}

/**
 * Class to instantiate tooltip campaign type object.
 */
export class CampaignTooltip implements CampaignType<CampaignTooltipConfig> {
    private static tooltipQueue = CampaignQueue.getInstance();
    private refToAttach = React.createRef();
    private static tooltipId = 0;
    constructor() {
        CampaignTooltip.tooltipId++;
    }

    public start(campaign: Campaign<CampaignTooltipConfig>) {
        CampaignTooltip.tooltipQueue.addToQueue(campaign);
    }

    public end(campaign: Campaign<CampaignTooltipConfig>, endSignal: CampaignEndSignal) {
        endCampaign(campaign, endSignal);
    }

    /**
     * This is called from the render function of the Campaign object
     * @param campaign Campaign object instance
     */
    public render(
        campaign: Campaign<CampaignTooltipConfig>,
        viewProps: CampaignTooltipConfig
    ): JSX.Element {
        const tooltipId: string = 'campaign-tooltip-' + CampaignTooltip.tooltipId;
        const { placement, timeout, placementRef, onRender } = viewProps;
        onRender?.();
        const tooltipContent: string = getTooltipContent(viewProps);

        const getTooltipComponent = (tooltipPlacementRef: React.RefObject<any>) => (
            <LazyTooltip
                content={tooltipContent}
                placement={placement}
                anchorRef={tooltipPlacementRef}
                timeout={timeout || DEFAULT_TIMEOUT_IN_MILLISECONDS}
                tooltipId={tooltipId}
                onDismiss={() => this.end(campaign, CampaignEndSignal.OnUserDismiss)}
            />
        );

        if (placementRef) {
            return getTooltipComponent(placementRef);
        } else {
            if (viewProps.refId) {
                const documentToAttachTo = document.getElementById(viewProps.refId);
                if (documentToAttachTo) {
                    (this.refToAttach as React.MutableRefObject<any>).current = documentToAttachTo;
                    return getTooltipComponent(this.refToAttach);
                } else {
                    this.end(campaign, CampaignEndSignal.OnNotRender);
                }
            } else {
                this.end(campaign, CampaignEndSignal.OnNotRender);
            }
        }
        return null;
    }
}
